import { getHelper } from '@/devtools/utils';
import { useBrokerStore } from '@/features/Broker/store';
import { RATING_TYPE } from '@/features/Broker/types';
import { VersicherungsnehmerPropertyContractSchema } from '@/features/Clients/Schemas/Versicherungsnehmer';
import type { ComparisonDetail } from '@/features/Comparison/Components/types';
import { ENTITY_TYPE } from '@/features/EntityManager/types';
import { SEARCH_DATA_CONTEXT } from '@/features/Global/types';
import type { PkvAdditionalUserSettings } from '@/features/Sparten/pkv/components/UserSettings/types';
import { pkvInputDehydrationDefinitions } from '@/features/Sparten/pkv/inputDehydration';
import {
  PKV_PREVIOUS_INSURANCE_TYPE,
  type PreviousInsuranceSchema,
  previousInsuranceSchema,
  productSchema,
  type ProductSchema,
} from '@/features/Sparten/pkv/schema/previousInsuranceSchema/previousInsuranceSchema';
import type {
  PkvAllTimeTariffsNotPublicInsured,
  PkvAllTimeTariffsSearchRequest,
} from '@/features/Sparten/pkv/types';
import type { InputStore } from '@/features/Sparten/pkv/useInputStore';
import { usePkvInputStore } from '@/features/Sparten/pkv/useInputStore';
import { usePkvPreviousInsuranceStore } from '@/features/Sparten/pkv/usePreviouseInsuranceInputStore';
import { featureFilterFunction } from '@/features/Sparten/pkv/utils/featureFilterFunction';
import { pkvAlternateTariffSearchRequestIdentifier } from '@/features/Sparten/pkv/utils/pkvAlternateTariffSearchRequestIdentifier';
import type { SpartenConfig } from '@/features/Sparten/types';
import {
  SORTABLE_ATTRIBUTES,
  sortingDefaults,
} from '@/features/Tariffs/sorting';
import { dehydrateRequestData } from '@/utils/dehydrateRequestData/dehydrateRequestData';
import { formatCurrency } from '@/utils/formatters/euro';
import string from '@/utils/formatters/string';
import { useStorableState } from '@/utils/useStorableState/useStorableState';
import get from 'lodash/get';
import set from 'lodash/set';
import { ValidationError } from 'yup';
import image from './assets/bg.jpg';
import { pkvVersicherungsangabenConfig } from '@/features/Sparten/pkv/versicherungsangabenConfig';

export const config: SpartenConfig<PkvAdditionalUserSettings> = {
  component: () => import('@/views/Base/Layout.vue'),
  path: '/pkv',

  props: {
    name: 'Krankenversicherung',
    sparte: 'pkv',
    image,
  },

  general: {
    maxSimultaneousCount: 3,

    versicherungsangabenConfig: pkvVersicherungsangabenConfig,

    clientFields: {
      versicherungsnehmer: {
        geburtsdatum: 'geburtsdatum',
        geschlecht: 'geschlecht',
      },
      versichertePerson: {
        geburtsdatum: 'geburtsdatum',
        geschlecht: 'geschlecht',
      },
    },

    calculationRelevantEntityFields: {
      [ENTITY_TYPE.CLIENT]: {
        versicherungsnehmer: ['geburtsdatum', 'geschlecht'],
        versichertePerson: ['geburtsdatum', 'geschlecht'],
      },
    },

    profileFields: [
      'versicherungsart',
      'leistungstraeger',
      'beihilfesaetze',
      'stationaer',
      'stationaerOptionen.leistung',
      'stationaerOptionen.gebuehrenordnung',
      'ambulant',
      'ambulantOptionen.gebuehrenordnung',
      'ambulantOptionen.heilpraktiker',
      'ambulantOptionen.kurort',
      'ambulantOptionen.logopaedie',
      'ambulantOptionen.ergotherapie',
      'ambulantOptionen.hilfsmittel',
      'ambulantOptionen.ergaenzungGKV',
      'zahn',
      'zahnOptionen.leistung',
      'zahnOptionen.gebuehrenordnung',
      'zahnOptionen.zahnSummenbegrenzungUnfall',
      'zahnOptionen.bruecken',
      'zahnOptionen.funktionsanalyse',
      'zahnOptionen.funktionstherapie',
      'zahnOptionen.implantate',
      'zahnOptionen.inlays',
      'zahnOptionen.kieferorthopaedie',
      'zahnOptionen.knochenaufbau',
      'zahnOptionen.kronen',
      'zahnOptionen.kunststofffuellungen',
      'zahnOptionen.onlays',
      'zahnOptionen.parodontosebehandlung',
      'zahnOptionen.zahnreinigung',
      'zahnOptionen.veneers',
      'zahnOptionen.verblendungen',
      'zahnOptionen.wurzelbehandlung',
      'zahnOptionen.zahnbehandlung',
      'zahnOptionen.zahnersatz',
      'zahnOptionen.zahnerstattung',
      'zahnOptionen.zahnbehandlungMin',
      'zahnOptionen.zahnersatzMin',
      'zahnOptionen.kieferorthopaedieMin',
      'zahnOptionen.materialLaborKosten',
      'krankentagegeld',
      'krankentagegeldOptionen.karenzzeit',
      'krankentagegeldOptionen.leistungBerufsunfaehigkeit',
      'krankentagegeldOptionen.leistungTeilBerufsunfaehigkeit',
      'krankentagegeldOptionen.alkoholKlausel',
      'krankentagegeldOptionen.tag0',
      'krankentagegeldOptionen.tag1',
      'krankentagegeldOptionen.tag2',
      'krankentagegeldOptionen.betrag0',
      'krankentagegeldOptionen.betrag1',
      'krankentagegeldOptionen.betrag2',
      'krankentagegeldOptionen.krankentageGeldFelderAnzahl',
      'krankenhaustagegeld',
      'krankenhaustagegeldOptionen.betrag',
      'auslandsreisekrankenversicherung',
      'auslandsreisekrankenversicherungOptionen.ruecktransportAusland',
      'pflegeBahrOptionen.auswahl',
      'pflegepflichtversicherung',
      'pflegepflichtversicherungOptionen.leistung',
      'pflegezusatz',
      'pflegezusatzOptionen.grad1',
      'pflegezusatzOptionen.grad2',
      'pflegezusatzOptionen.grad3',
      'pflegezusatzOptionen.grad4',
      'pflegezusatzOptionen.grad5',
      'pflegezusatzOptionen.pflegeBahrAufbauTarife',
      'pflegezusatzOptionen.pflegeBahrTarife',
      'pflegezusatzOptionen.pflegekostenTarife',
      'allgemein.selbstbeteiligungMin',
      'allgemein.selbstbeteiligungMax',
      'allgemein.optionsrecht',
      'allgemein.beitragsrueckerstattung',
      'allgemein.pauschalleistung',
      'allgemein.primaerarzttarif',
      'allgemein.osttarife',
      'allgemein.beitragsentlastungOptionen.betrag',
      'allgemein.beitragsentlastungOptionen.typ',
      'allgemein.altersrueckstellung',
    ],

    defaultUserSettings: {
      bewertungsart: RATING_TYPE.ERFUELLUNGSGRAD,
      gesamtaufwand: true,
    },

    userSettingsComponent: () =>
      import('@/features/Sparten/pkv/components/UserSettings/UserSettings.vue'),

    featureFilterFunction,

    buildInputDataObject: (context: SEARCH_DATA_CONTEXT, dehydrate = false) => {
      const state = usePkvInputStore().$state as InputStore;

      const preparedData = useStorableState().create(state);

      if (context === SEARCH_DATA_CONTEXT.TARIFF && dehydrate) {
        return dehydrateRequestData(
          preparedData,
          pkvInputDehydrationDefinitions,
        );
      }

      return preparedData;
    },

    buildContractDataObject: (state) => state,

    allTimeTariffTransformationCallback: (isQuoteContext: boolean = false) => {
      const previousInsuranceStore = usePkvPreviousInsuranceStore();

      if (isQuoteContext) {
        return previousInsuranceStore.$state;
      }

      if (previousInsuranceStore.data.length === 0) {
        return undefined;
      }

      if (!previousInsuranceStore.aktiv) {
        return undefined;
      }

      let additionalInformationWhenNotPublicInsured:
        | PkvAllTimeTariffsNotPublicInsured
        | undefined;
      if (previousInsuranceStore.art !== PKV_PREVIOUS_INSURANCE_TYPE.PUBLIC) {
        additionalInformationWhenNotPublicInsured = {
          gesellschaft:
            previousInsuranceStore.gesellschaft === null
              ? undefined
              : previousInsuranceStore.gesellschaft.toString(),
          berufsgruppe: previousInsuranceStore.berufsgruppe,
          berufsstatus: previousInsuranceStore.berufsstatus,
        };
      } else {
        additionalInformationWhenNotPublicInsured = undefined;
      }

      const data: PkvAllTimeTariffsSearchRequest = {
        beitrag: previousInsuranceStore.beitrag,
        einzelbeitraege: previousInsuranceStore.einzelbeitraege,
        products: previousInsuranceStore.tarifBausteine.map(
          (product: ProductSchema) => ({
            id: product.id,
            name: product.name,
            price:
              product.price === null ||
              product.price === undefined ||
              product.price === 0
                ? null
                : product.price,
          }),
        ),
        arbeitgeberanteil: {
          kv: previousInsuranceStore.arbeitsgeberanteil.kv,
          ppv: previousInsuranceStore.arbeitsgeberanteil.ppv,
        },
        art: previousInsuranceStore.art,
        ...additionalInformationWhenNotPublicInsured,
      };

      return data;
    },

    quoteLoadAllTimeTariffCallback: (data: Record<string, unknown>) => {
      const insuranceData = data as PreviousInsuranceSchema;
      try {
        // @todo -> remove when done https://demvsystems.atlassian.net/browse/VER-3301
        if (Array.isArray(insuranceData.tarifBausteine)) {
          const defaultValues = productSchema.getDefault();

          insuranceData.tarifBausteine.forEach((baustein, index) => {
            insuranceData.tarifBausteine[index] = {
              ...defaultValues,
              ...baustein,
            };
          });
        }

        previousInsuranceSchema.validateSync(insuranceData, {
          abortEarly: false,
        });
      } catch (error) {
        // @todo centralize this functionality
        if (error instanceof ValidationError) {
          const defaultValues = previousInsuranceSchema.getDefault();

          if (error.inner.length === 0) {
            if (error.path !== undefined) {
              set(
                data,
                error.path,
                get(defaultValues, error.path, get(data, error.path)),
              );
            }
          } else {
            error.inner.forEach((innerError) => {
              if (innerError.path !== undefined) {
                set(
                  data,
                  innerError.path,
                  get(
                    defaultValues,
                    innerError.path,
                    get(data, innerError.path),
                  ),
                );
              }
            });
          }
        }
      } finally {
        usePkvPreviousInsuranceStore().$patch(insuranceData);
      }
    },

    getAlternateTariffSearchRequestIdentifier:
      pkvAlternateTariffSearchRequestIdentifier,

    comparisonComponents: {
      GeneralTariffInfoComponent: () =>
        import(
          '@/features/Sparten/pkv/components/ComparisonTariffInfo/ComparisonTariffInfo.vue'
        ),
      TariffServiceComponent: () =>
        import(
          '@/features/Sparten/pkv/components/ComparisonTariffService/ComparisonTariffService.vue'
        ),
    },

    requiresBankTransferInformation: () => true,

    disableRisikoDEMVer: true,

    comparison: {
      getDetails: () => {
        const map = new Map<string, ComparisonDetail>();

        map.set('betrag', {
          label: 'zu zahlender Beitrag',
          format: formatCurrency,
        });

        return map;
      },
      serviceFields: [
        // see custom ComparisonTariffService component
        {
          label: 'Tarife',
          fieldPath: '',
          formatter: string,
        },
      ],
    },

    getSortableGlobalsOverride: () => {
      if (useBrokerStore().settings.data?.bewertungsart === RATING_TYPE.STARS) {
        return [
          SORTABLE_ATTRIBUTES.RATING,
          SORTABLE_ATTRIBUTES.BEITRAG,
          SORTABLE_ATTRIBUTES.GESELLSCHAFT,
          SORTABLE_ATTRIBUTES.TARIFNAME,
        ];
      }

      return [];
    },

    getSortingDefaults: () => {
      if (useBrokerStore().settings.data?.bewertungsart === RATING_TYPE.STARS) {
        return [SORTABLE_ATTRIBUTES.RATING, SORTABLE_ATTRIBUTES.BEITRAG];
      }

      return sortingDefaults;
    },

    baseTariffComponent: () =>
      import('@/features/Sparten/pkv/components/TariffDetails/BaseTariff.vue'),

    tariffComponent: () =>
      import(
        '@/features/Sparten/pkv/components/TariffDetails/TariffDetails.vue'
      ),

    devtools: {
      directAccess: {
        input: {
          icon: 'edit',
          setup: async () => getHelper('pkv').resetState(),
        },
        offers: {
          icon: 'list',
          setup: async () => getHelper('pkv').selectAllFeatures(),
        },
        comparison: {
          icon: 'compare_arrows',
          setup: async () => getHelper('pkv').loadTariffs(),
        },
        contract: {
          icon: 'description',
          setup: async () => getHelper('pkv').showContract(),
        },
      },
    },

    contractVnSchema: () => VersicherungsnehmerPropertyContractSchema,
  },

  // Wizard steps
  steps: {
    input: {
      component: () => import('@/features/Sparten/pkv/views/InputView.vue'),
    },

    offers: {
      component: () => import('@/features/Sparten/pkv/views/Tariffs.vue'),
    },

    comparison: {
      component: () => import('@/features/Sparten/pkv/views/Comparison.vue'),
      meta: {
        showContractButton: true,
      },
    },

    contract: {
      component: () => import('@/features/Sparten/pkv/views/ContractView.vue'),
    },

    completion: {
      component: () => import('@/views/Layouts/Completion/Completion.vue'),
    },
  },
};

export default config;
